import { sortBy } from 'lodash-es';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import Stepper from './Stepper';
import { RcsSteps } from './constants';
import { RcsCampaignFormValues } from './types';
import {
  CarrierTermsForm,
  CarrierTermsFormValues,
  ComplianceForm,
  ComplianceFormValues,
  LaunchDetailsForm,
  LaunchDetailsFormValues,
  RcsCampaignDetailsForm,
  RcsCampaignDetailsFormValues,
  RcsCampaignRegisteredModal,
} from '../../components';
import {
  CampaignBuilderMetadata,
  CampaignBuilderResult,
  CnpItem,
  MnoMetadata,
  Reseller,
  UseCase,
  campaignBuilderController,
  enumController,
  resellerController,
} from '@/apis/core';
import { RCS_CAMPAIGNS_PATH } from '@/constants/paths';
import { sortMnoNestedObjectToList } from '@/utils';

interface RcsCampaignRegistrationProps {
  brandUid: string;
  hostingRegion: string;
  billingCategory: string;
  useCase: UseCase;
  metadata?: CampaignBuilderMetadata;
  cnps?: CnpItem[];
  value: RcsCampaignFormValues;
  onChange: (value: RcsCampaignFormValues) => void;
  onPrevious?: () => void;
}

const RcsCampaignRegistration: FunctionComponent<
  RcsCampaignRegistrationProps
> = ({
  brandUid,
  hostingRegion,
  billingCategory,
  useCase,
  metadata,
  cnps,
  value,
  onChange,
  onPrevious,
}) => {
  const history = useHistory();
  const [mnos, setMnos] = useState<MnoMetadata[]>([]);
  const [resellers, setResellers] = useState<Reseller[]>([]);
  const [step, setStep] = useState(1);
  const [result, setResult] = useState<CampaignBuilderResult>();
  const [modalOpen, setModalOpen] = useState(false);
  const [tenDlcUseCases, setTenDlcUseCases] = useState<UseCase[]>([]);
  const {
    rcsCampaignDetailsFormValues,
    carrierTermsFormValues,
    launchDetailsFormValues,
    complianceFormValues,
  } = value;

  const selectedMnos = useMemo(
    () => mnos.filter((mno) => carrierTermsFormValues.mnoIds.includes(mno.id)),
    [mnos, carrierTermsFormValues.mnoIds]
  );

  const handleRcsCampaignDetailsFormValuesChange = (
    rcsCampaignDetailsFormValues: RcsCampaignDetailsFormValues
  ) => {
    onChange({
      ...value,
      rcsCampaignDetailsFormValues,
    });
  };

  const handleCarrierTermsFormValuesChange = (
    carrierTermsFormValues: CarrierTermsFormValues
  ) => {
    onChange({
      ...value,
      carrierTermsFormValues,
    });
  };

  const handleLaunchDetailsFormValuesChange = (
    launchDetailsFormValues: LaunchDetailsFormValues
  ) => {
    onChange({
      ...value,
      launchDetailsFormValues,
    });
  };

  const handleComplianceFormValuesChange = (
    complianceFormValues: ComplianceFormValues
  ) => {
    onChange({
      ...value,
      complianceFormValues,
    });
  };

  const handleResellerAdd = (reseller: Reseller) => {
    const newResellers = sortBy([...resellers, reseller], (reseller) =>
      reseller.companyName.toLowerCase()
    );
    setResellers(newResellers);
  };

  const handlePrevious = () => {
    setStep(step - 1);
  };

  const handleNext = () => {
    setStep(step + 1);
  };

  const handleComplete = async () => {
    const samples: Record<string, string> = {};
    Object.values(complianceFormValues.samples).forEach((sample, index) => {
      samples[`sample${index + 1}`] = sample.message;
    });
    const result = await campaignBuilderController.createCampaign(
      brandUid,
      useCase.id,
      complianceFormValues.cnpUid,
      {
        channelId: 'RCS',
        hostingRegion,
        billingCategory,
        ...rcsCampaignDetailsFormValues,
        ...carrierTermsFormValues,
        ...launchDetailsFormValues,
        ...complianceFormValues,
        color: rcsCampaignDetailsFormValues.color.slice(1),
        phones: rcsCampaignDetailsFormValues.phonePairs.map(
          (phonePair) => phonePair.phone
        ),
        phoneLabels: rcsCampaignDetailsFormValues.phonePairs.map(
          (phonePair) => phonePair.label
        ),
        emails: rcsCampaignDetailsFormValues.emailPairs.map(
          (emailPair) => emailPair.email
        ),
        emailLabels: rcsCampaignDetailsFormValues.emailPairs.map(
          (emailPair) => emailPair.label
        ),
        websites: rcsCampaignDetailsFormValues.websitePairs.map(
          (websitePair) => websitePair.website
        ),
        websiteLabels: rcsCampaignDetailsFormValues.websitePairs.map(
          (websitePair) => websitePair.label
        ),
        ...samples,
        optinKeywords:
          complianceFormValues.optinKeywords.length > 0
            ? complianceFormValues.optinKeywords.join(',')
            : null,
        optoutKeywords:
          complianceFormValues.optoutKeywords.length > 0
            ? complianceFormValues.optoutKeywords.join(',')
            : null,
        helpKeywords:
          complianceFormValues.helpKeywords.length > 0
            ? complianceFormValues.helpKeywords.join(',')
            : null,
      }
    );
    if (result) {
      setResult(result);
      setModalOpen(true);
    }
  };

  const handleModalClose = async () => {
    setModalOpen(false);
    if (result) {
      history.push(`${RCS_CAMPAIGNS_PATH}/${result.campaignUid}`);
    }
  };

  useEffect(() => {
    (async () => {
      const response = await resellerController.getResellers({
        recordsPerPage: 1000,
      });
      if (response) {
        setResellers(response.records);
      }

      const useCases = await enumController.getUseCases({ channelId: '10DLC' });
      setTenDlcUseCases(useCases);
    })();
  }, []);

  useEffect(() => {
    if (metadata) {
      const mnos = sortMnoNestedObjectToList(
        metadata.mnoMetadata
      ) as MnoMetadata[];
      setMnos(mnos);
    } else {
      setMnos([]);
    }
  }, [metadata]);

  return (
    <>
      <Stepper activeIndex={step} steps={RcsSteps} />
      {step === 1 ? (
        <RcsCampaignDetailsForm
          brandUid={brandUid}
          defaultValues={rcsCampaignDetailsFormValues}
          onChange={handleRcsCampaignDetailsFormValuesChange}
          onPrevious={onPrevious}
          onNext={handleNext}
        />
      ) : step === 2 ? (
        <CarrierTermsForm
          carriers={mnos}
          defaultValues={carrierTermsFormValues}
          onChange={handleCarrierTermsFormValuesChange}
          onPrevious={handlePrevious}
          onNext={handleNext}
        />
      ) : step === 3 ? (
        <LaunchDetailsForm
          useCase={useCase}
          defaultValues={launchDetailsFormValues}
          onChange={handleLaunchDetailsFormValuesChange}
          onPrevious={handlePrevious}
          onNext={handleNext}
        />
      ) : step === 4 ? (
        <ComplianceForm
          brandUid={brandUid}
          mnos={selectedMnos}
          cnps={cnps}
          resellers={resellers}
          tenDlcUseCases={tenDlcUseCases}
          defaultValues={complianceFormValues}
          onResellerAdd={handleResellerAdd}
          onChange={handleComplianceFormValuesChange}
          onPrevious={handlePrevious}
          onComplete={handleComplete}
        />
      ) : null}
      <RcsCampaignRegisteredModal open={modalOpen} onClose={handleModalClose} />
    </>
  );
};

export default RcsCampaignRegistration;
